import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Skeleton,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  WhatsApp as WhatsAppIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PhotoCamera as CameraIcon,
  SendRounded as SendIcon,
  SmartToy as BotIcon,
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon,
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import WhatsAppPhoneModal from "./WhatsAppPhoneModal";
import CustomConfirmationDialog from "../../dialogs/CustomConfirmationDialog";
import DeliveryCenterCostsService from "../../../api/DeliveryCenter/deliveryCenterCosts";

const ACCENT = "#059669";
const ACCENT_LIGHT = "#10b981";
const ACCENT_DARK = "#047857";
const ACCENT_BG = "#064e3b";

const TUTORIAL_STEPS = [
  {
    icon: PhoneIcon,
    number: "01",
    title: "Cadastre o numero",
    description:
      'Clique em "Novo Numero" e registre o telefone que enviara fotos de notas fiscais para o bot.',
  },
  {
    icon: CameraIcon,
    number: "02",
    title: "Fotografe a nota fiscal",
    description:
      "Tire uma foto clara da nota fiscal com o celular cadastrado. Garanta boa iluminacao e que todos os dados estejam legiveis.",
  },
  {
    icon: SendIcon,
    number: "03",
    title: "Envie via WhatsApp",
    description:
      "Envie a foto para o numero do bot via WhatsApp. O bot ira processar automaticamente a imagem recebida.",
  },
  {
    icon: BotIcon,
    number: "04",
    title: "Lancamento automatico",
    description:
      "O bot le a nota fiscal usando IA, extrai os dados e lanca automaticamente no sistema de custos.",
  },
];

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 260, damping: 24 },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.3 },
  }),
  removed: {
    opacity: 0,
    x: -100,
    transition: { duration: 0.2 },
  },
};

const WhatsAppBotManagement = () => {
  const theme = useTheme();
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [tutorialOpen, setTutorialOpen] = useState(true);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  const fetchPhoneNumbers = async () => {
    setLoading(true);
    try {
      const response = await DeliveryCenterCostsService.getBotPhoneNumbers();
      const phoneNumbersData = response.data.results;
      setPhoneNumbers(phoneNumbersData);
    } catch (error) {
      console.error("Erro ao buscar numeros de telefone:", error);
      showAlert(
        "Falha ao carregar numeros de telefone. Por favor, tente novamente.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhoneNumber = () => {
    setCurrentItem(null);
    setEditMode(false);
    setModalOpen(true);
  };

  const handleEditPhoneNumber = (phoneNumber) => {
    setCurrentItem(phoneNumber);
    setEditMode(true);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentItem(null);
  };

  const handleDeletePhoneNumber = async (phoneNumberId) => {
    try {
      await DeliveryCenterCostsService.deleteBotPhoneNumber(phoneNumberId);
      showAlert("Numero de telefone excluido com sucesso!", "success");
      fetchPhoneNumbers();
    } catch (error) {
      console.error("Erro ao excluir numero de telefone:", error);
      showAlert(
        "Falha ao excluir numero de telefone. Por favor, tente novamente.",
        "error"
      );
    }
  };

  const handleModalSuccess = (message) => {
    showAlert(message, "success");
    fetchPhoneNumbers();
    handleCloseModal();
  };

  const showAlert = (message, severity = "success") => {
    setAlertInfo({ open: true, message, severity });
    setTimeout(() => {
      setAlertInfo((prev) => ({ ...prev, open: false }));
    }, 5000);
  };

  const formatPhoneNumber = (item) => {
    return `+${item.country_code} (${item.area_code}) ${item.phone_number}`;
  };

  const transformedPhoneNumbers = phoneNumbers.map((phone) => ({
    ...phone,
    displayName: formatPhoneNumber(phone),
    formattedPhone: formatPhoneNumber(phone),
  }));

  const filteredItems = transformedPhoneNumbers.filter((item) =>
    item.formattedPhone.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      handleDeletePhoneNumber(itemToDelete.id);
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Alert */}
      <Collapse in={alertInfo.open}>
        <Alert
          severity={alertInfo.severity}
          onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
          sx={{
            mb: 3,
            borderRadius: 2,
            ...(alertInfo.severity === "success" && {
              backgroundColor: alpha(ACCENT, 0.08),
              color: ACCENT_DARK,
              "& .MuiAlert-icon": { color: ACCENT },
            }),
          }}
        >
          {alertInfo.message}
        </Alert>
      </Collapse>

      {/* ─── TUTORIAL SECTION ─── */}
      <motion.div variants={staggerContainer} initial="hidden" animate="visible">
        <motion.div variants={fadeUp}>
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(ACCENT, 0.15)}`,
            }}
          >
            {/* Tutorial header */}
            <Box
              onClick={() => setTutorialOpen(!tutorialOpen)}
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                background: `linear-gradient(135deg, ${ACCENT_BG} 0%, ${ACCENT_DARK} 50%, ${ACCENT} 100%)`,
                transition: "all 0.2s ease",
                "&:hover": {
                  background: `linear-gradient(135deg, ${ACCENT_BG} 0%, ${ACCENT_DARK} 40%, ${ACCENT_LIGHT} 100%)`,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: alpha("#fff", 0.15),
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 20, color: "#25D366" }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: "0.85rem",
                      lineHeight: 1.3,
                    }}
                  >
                    Como funciona o Bot WhatsApp
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha("#fff", 0.55),
                      fontSize: "0.68rem",
                    }}
                  >
                    Aprenda a lancar notas fiscais automaticamente
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" sx={{ color: alpha("#fff", 0.6) }}>
                {tutorialOpen ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Box>

            {/* Tutorial body */}
            <Collapse in={tutorialOpen}>
              <Box
                sx={{
                  px: 3,
                  py: 3,
                  background: alpha(ACCENT, 0.02),
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      lg: "1fr 1fr 1fr 1fr",
                    },
                    gap: 2.5,
                  }}
                >
                  {TUTORIAL_STEPS.map((step, idx) => {
                    const StepIcon = step.icon;
                    return (
                      <motion.div
                        key={idx}
                        variants={fadeUp}
                        whileHover={{ y: -3 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            p: 2.5,
                            borderRadius: 2.5,
                            border: `1px solid ${alpha(ACCENT, 0.12)}`,
                            background: theme.palette.background.paper,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: alpha(ACCENT, 0.3),
                              boxShadow: `0 4px 16px ${alpha(ACCENT, 0.08)}`,
                            },
                          }}
                        >
                          {/* Step number */}
                          <Typography
                            variant="caption"
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 14,
                              fontSize: "0.6rem",
                              fontWeight: 800,
                              letterSpacing: "1px",
                              color: alpha(ACCENT, 0.25),
                            }}
                          >
                            {step.number}
                          </Typography>

                          {/* Icon */}
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: 2,
                              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha(ACCENT_LIGHT, 0.15)} 100%)`,
                            }}
                          >
                            <StepIcon
                              sx={{ fontSize: 20, color: ACCENT }}
                            />
                          </Box>

                          {/* Content */}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.82rem",
                              color: theme.palette.text.primary,
                              mb: 0.75,
                              lineHeight: 1.3,
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.72rem",
                              color: theme.palette.text.secondary,
                              lineHeight: 1.5,
                            }}
                          >
                            {step.description}
                          </Typography>

                          {/* Connector arrow (not on last) */}
                          {idx < TUTORIAL_STEPS.length - 1 && (
                            <Box
                              sx={{
                                display: { xs: "none", lg: "flex" },
                                position: "absolute",
                                right: -18,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 2,
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                alignItems: "center",
                                justifyContent: "center",
                                background: theme.palette.background.paper,
                                border: `1px solid ${alpha(ACCENT, 0.15)}`,
                                boxShadow: `0 1px 4px ${alpha(ACCENT, 0.1)}`,
                              }}
                            >
                              <ArrowIcon
                                sx={{
                                  fontSize: 14,
                                  color: alpha(ACCENT, 0.5),
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </motion.div>
                    );
                  })}
                </Box>

                {/* Quick tip */}
                <Box
                  sx={{
                    mt: 2.5,
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    background: alpha(ACCENT, 0.06),
                    border: `1px dashed ${alpha(ACCENT, 0.2)}`,
                  }}
                >
                  <CheckIcon sx={{ fontSize: 18, color: ACCENT }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.72rem",
                      color: theme.palette.text.secondary,
                      lineHeight: 1.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: ACCENT_DARK }}
                    >
                      Dica:
                    </Box>{" "}
                    Fotografe a nota em local bem iluminado, sem sombras, e
                    com todos os cantos visiveis. O bot processa melhor
                    imagens com boa qualidade.
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </Paper>
        </motion.div>

        {/* ─── PHONE NUMBERS LIST ─── */}
        <motion.div variants={fadeUp}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* List header */}
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `1px solid ${theme.palette.divider}`,
                background: alpha(ACCENT, 0.03),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                >
                  Numeros Autorizados
                </Typography>
                <Chip
                  label={loading ? "..." : phoneNumbers.length}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    backgroundColor: alpha(ACCENT, 0.1),
                    color: ACCENT,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title="Atualizar lista">
                  <IconButton
                    onClick={fetchPhoneNumbers}
                    disabled={loading}
                    size="small"
                    sx={{
                      color: ACCENT,
                      "&:hover": { backgroundColor: alpha(ACCENT, 0.08) },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddPhoneNumber}
                  disabled={loading}
                  size="small"
                  sx={{
                    ml: 0.5,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
                    boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
                    "&:hover": {
                      boxShadow: `0 4px 14px ${alpha(ACCENT, 0.4)}`,
                    },
                  }}
                >
                  Novo Numero
                </Button>
              </Box>
            </Box>

            {/* Search bar */}
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <TextField
                fullWidth
                placeholder="Buscar numeros de telefone..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 18, color: theme.palette.text.disabled }}
                      />
                    </InputAdornment>
                  ),
                  ...(searchTerm && {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm("")}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.82rem",
                    "&:hover fieldset": {
                      borderColor: alpha(ACCENT, 0.3),
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: ACCENT,
                    },
                  },
                }}
              />
            </Box>

            {/* List content */}
            <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
              {loading ? (
                <Box sx={{ p: 2.5 }}>
                  {[...Array(3)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 1.5,
                      }}
                    >
                      <Skeleton
                        variant="circular"
                        width={36}
                        height={36}
                        sx={{ flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="60%" height={20} />
                        <Skeleton width="35%" height={16} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : filteredItems.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 6,
                    px: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: alpha(ACCENT, 0.06),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <WhatsAppIcon
                      sx={{ fontSize: 28, color: alpha(ACCENT, 0.35) }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      mb: 0.5,
                    }}
                  >
                    Nenhum numero autorizado
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.disabled,
                      textAlign: "center",
                      maxWidth: 280,
                      mb: 2.5,
                    }}
                  >
                    Cadastre o primeiro numero para comecar a usar o bot de
                    leitura automatica de notas fiscais.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddPhoneNumber}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
                      boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
                      "&:hover": {
                        boxShadow: `0 4px 14px ${alpha(ACCENT, 0.4)}`,
                      },
                    }}
                  >
                    Novo Numero
                  </Button>
                </Box>
              ) : (
                <List disablePadding>
                  <AnimatePresence>
                    {filteredItems
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((item, index) => (
                        <React.Fragment key={item.id}>
                          <motion.div
                            initial="hidden"
                            animate="visible"
                            exit="removed"
                            variants={listItemVariants}
                            custom={index}
                            layoutId={`whatsapp-item-${item.id}`}
                          >
                            <ListItem
                              sx={{
                                px: 2.5,
                                py: 1.5,
                                transition: "background 0.15s ease",
                                "&:hover": {
                                  backgroundColor: alpha(ACCENT, 0.03),
                                },
                              }}
                            >
                              {/* WhatsApp icon */}
                              <Box
                                sx={{
                                  mr: 2,
                                  width: 36,
                                  height: 36,
                                  borderRadius: 2,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  background: alpha("#25D366", 0.1),
                                }}
                              >
                                <WhatsAppIcon
                                  sx={{
                                    fontSize: 18,
                                    color: "#25D366",
                                  }}
                                />
                              </Box>

                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      {item.formattedPhone}
                                    </Typography>
                                    {item.status && (
                                      <Chip
                                        size="small"
                                        label={item.status}
                                        sx={{
                                          height: 20,
                                          fontSize: "0.6rem",
                                          fontWeight: 600,
                                          backgroundColor: alpha(
                                            ACCENT,
                                            0.1
                                          ),
                                          color: ACCENT,
                                          border: `1px solid ${alpha(ACCENT, 0.2)}`,
                                        }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  item.deliveryCenterName ? (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.72rem",
                                        color:
                                          theme.palette.text.disabled,
                                      }}
                                    >
                                      {item.deliveryCenterName}
                                    </Typography>
                                  ) : null
                                }
                              />

                              <ListItemSecondaryAction>
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditPhoneNumber(item)
                                    }
                                    sx={{
                                      mr: 0.5,
                                      color: ACCENT,
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          ACCENT,
                                          0.08
                                        ),
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteClick(item)
                                    }
                                    sx={{
                                      color:
                                        theme.palette.error.main,
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          theme.palette.error.main,
                                          0.08
                                        ),
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                          </motion.div>
                          {index <
                            filteredItems.slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            ).length -
                              1 && (
                            <Divider
                              component="li"
                              sx={{ mx: 2.5 }}
                            />
                          )}
                        </React.Fragment>
                      ))}
                  </AnimatePresence>
                </List>
              )}
            </Box>

            {/* Pagination */}
            {filteredItems.length > 0 && (
              <TablePagination
                component="div"
                count={filteredItems.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Itens por pagina:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                  "& .MuiTablePagination-selectIcon": {
                    color: theme.palette.text.disabled,
                  },
                }}
              />
            )}
          </Paper>
        </motion.div>
      </motion.div>

      {/* Confirmation Dialog */}
      <CustomConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusao"
        dialogContent={`Tem certeza que deseja excluir "${itemToDelete?.formattedPhone}"?`}
        confirmText="Excluir"
        cancelText="Cancelar"
      />

      {/* Phone Number Modal */}
      <WhatsAppPhoneModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        editMode={editMode}
        initialData={currentItem}
        onSuccess={handleModalSuccess}
      />
    </Box>
  );
};

export default WhatsAppBotManagement;
